// client/src/components/pages/Geofencing/geofenceConfig.ts (converted from .js, ST-00269)
// Pure helpers for the LexiStar versioned geofence configuration (contracts
// m1-contracts.md §2.3): the editor's form state <-> the API payload, and the
// field diff between two versions for the history drawer. No React here, so the
// mapping is unit-testable on its own.
//
// ST-00269 (the legacy geofence evaluator is retired): the zone form no longer sends the
// legacy alert settings (alertOnEntry / alertOnExit / dwellTimeMinutes) in any mode — a zone's
// alerts are its rules, which the LexiStar engine evaluates. rulesFromLegacy stays: it reads
// the deprecated columns of a row saved before 021 gave it rules. zoneRuleSummary words a
// zone's rules for the zone list.
//
// ST-00282: the zone's Alerts switch (`alertsEnabled`, migration 072). ONE model: the switch says
// WHETHER the zone alerts at all; the rules say WHICH crossings and rule breaks alert while it is
// on. Off keeps the rules (switching back on restores them) and records everything without
// alerting. A no-go zone is always on (zoneAlertsLocked). A new zone starts off. The server is the
// source of truth: these helpers only word and send the user's choice.
import { toApiDateString, formatAppDateTime } from '../../../utils/dateUtils';
import {
  DEFAULT_PURPOSE,
  ROLE_PURPOSES,
  ZONE_TO_PURPOSE,
  defaultRolesFor,
  getPurposeMeta,
  purposeOf,
} from './purposeTypes';
import type { GeofenceVersionedForm, RuleFormRow, ScheduleWindow } from './GeofenceConfigFields';
import type {
  GeofenceRow,
  GeofenceRuleRow,
  LatLngPair,
} from '../../../types/clientPages/geofencing';

type Row = Record<string, unknown>;

export const SCHEDULE_TZ = 'Africa/Johannesburg';
export const DEFAULT_DEBOUNCE = { entry_fixes: 2, entry_seconds: 30, exit_buffer_m: 30 };
export const DEFAULT_WINDOW: ScheduleWindow = { days: [1, 2, 3, 4, 5], from: '06:00', to: '18:00' };

export const SEVERITIES = [
  { id: 'I', label: 'Info' },
  { id: 'L', label: 'Low' },
  { id: 'M', label: 'Medium' },
  { id: 'H', label: 'High' },
  { id: 'C', label: 'Critical' },
];

// ISO weekdays, 1 = Monday (the contract's schedule.windows[].days).
export const WEEKDAYS = [
  { id: 1, short: 'Mon' },
  { id: 2, short: 'Tue' },
  { id: 3, short: 'Wed' },
  { id: 4, short: 'Thu' },
  { id: 5, short: 'Fri' },
  { id: 6, short: 'Sat' },
  { id: 7, short: 'Sun' },
];

/** One rule kind the editor offers (`threshold`: the key it edits, in which unit). */
export interface RuleKind {
  on: string;
  label: string;
  severity: string;
  threshold?: {
    key: 'threshold_s' | 'limit_kmh';
    unit: string;
    scale: number;
    placeholder: string;
  };
  direction?: boolean;
}

// The FR-GEO-004 subset the M1 engine evaluates, in @lexistar/domain's
// GeofenceRuleV1 shape. `threshold`: which rule key the form edits and in what
// unit (dwell thresholds are stored in seconds and edited in minutes).
// `direction`: after_hours checks entries, exits or both against the zone's
// schedule (its operating hours), so it needs one.
export const RULE_KINDS: RuleKind[] = [
  { on: 'entry', label: 'On entry', severity: 'I' },
  { on: 'exit', label: 'On exit', severity: 'I' },
  {
    on: 'dwell_over',
    label: 'Dwell longer than',
    severity: 'M',
    threshold: { key: 'threshold_s', unit: 'min', scale: 60, placeholder: '120' },
  },
  {
    on: 'dwell_under',
    label: 'Dwell shorter than',
    severity: 'M',
    threshold: { key: 'threshold_s', unit: 'min', scale: 60, placeholder: '5' },
  },
  {
    on: 'speed_over',
    label: 'Speed above',
    severity: 'M',
    threshold: { key: 'limit_kmh', unit: 'km/h', scale: 1, placeholder: '20' },
  },
  { on: 'after_hours', label: 'Outside operating hours', severity: 'M', direction: true },
];

export const AFTER_HOURS_DIRECTIONS = [
  { id: 'both', label: 'Entry or exit' },
  { id: 'entry', label: 'Entry' },
  { id: 'exit', label: 'Exit' },
];

/** ST-00282: the purpose whose Alerts switch is locked on. */
export const NO_GO_PURPOSE = 'no_go';
/**
 * Why a no-go zone's switch cannot be turned off (one line, shown with the locked switch), for a
 * zone without a schedule; nogoAlertsReason names the hours of one that has.
 */
export const NOGO_ALERTS_REASON = 'A no-go zone always alerts: every entry raises a critical alert.';

/**
 * ST-00282: NOGO_ALERTS_REASON for a no-go zone with this schedule (its no-go hours): "… every
 * entry between 18:00 and 06:00 raises a critical alert", with the days when not every day and
 * the time zone when not SAST. No schedule (or no windows): NOGO_ALERTS_REASON.
 */
export function nogoAlertsReason(
  schedule: { tz?: string | null; windows?: ScheduleWindow[] | null } | null | undefined
): string {
  const windows = schedule?.windows || [];
  if (!windows.length) return NOGO_ALERTS_REASON;
  const tz = schedule?.tz && schedule.tz !== 'Africa/Johannesburg' ? ` (${schedule.tz} time)` : '';
  if (windows.length === 1) {
    const w = windows[0]!;
    const days = dayList(w.days);
    const on = days === 'Every day' ? '' : ` on ${days}`;
    return `A no-go zone always alerts: every entry between ${w.from} and ${w.to}${on}${tz} raises a critical alert.`;
  }
  return `A no-go zone always alerts: every entry in its hours (${windowsText({ windows })})${tz} raises a critical alert.`;
}

/** ST-00282: the zone's Alerts switch cannot be turned off (it is a no-go zone). */
export const zoneAlertsLocked = (purposeType: string | null | undefined): boolean =>
  purposeType === NO_GO_PURPOSE;

/**
 * ST-00282: the Alerts switch as the zone list and the editor show it: a no-go zone always on;
 * otherwise the server's AlertsEnabled (absent before migration 072: on, the rules decide).
 */
export const zoneAlertsOn = (g: Pick<GeofenceRow, 'AlertsEnabled'> & { PurposeType?: string | null }): boolean =>
  zoneAlertsLocked(g.PurposeType) || g.AlertsEnabled !== false;

export const defaultSeverity = (on: string, purposeType?: string | null): string =>
  on === 'entry' && purposeType === 'no_go'
    ? 'C'
    : RULE_KINDS.find((k) => k.on === on)?.severity || 'M';

const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Rules implied by the legacy columns (what the 021 backfill writes): only for a row saved
 * before 021 gave it rules. The columns are deprecated mirrors since ST-00269.
 */
export function rulesFromLegacy(
  g: Pick<GeofenceRow, 'AlertOnEntry' | 'AlertOnExit' | 'DwellTimeMinutes'> | null | undefined,
  purposeType?: string | null
): GeofenceRuleRow[] {
  const rules: GeofenceRuleRow[] = [];
  if (g?.AlertOnEntry !== false)
    rules.push({ on: 'entry', severity: defaultSeverity('entry', purposeType) });
  if (g?.AlertOnExit !== false) rules.push({ on: 'exit', severity: 'I' });
  const dwell = Number(g?.DwellTimeMinutes);
  if (Number.isFinite(dwell) && dwell > 0) {
    rules.push({ on: 'dwell_over', threshold_s: Math.round(dwell * 60), severity: 'M' });
  }
  return rules;
}

/** Rules array -> per-kind editor rows. */
export function rulesToForm(
  rules: readonly GeofenceRuleRow[] | null | undefined,
  purposeType?: string | null
): Record<string, RuleFormRow> {
  const byKind: Record<string, GeofenceRuleRow> = Object.fromEntries(
    (Array.isArray(rules) ? rules : []).map((r) => [r.on, r])
  );
  return Object.fromEntries(
    RULE_KINDS.map((k) => {
      const r = byKind[k.on];
      const row: RuleFormRow = {
        enabled: !!r,
        severity: r?.severity || defaultSeverity(k.on, purposeType),
      };
      if (k.threshold) {
        const raw = r?.[k.threshold.key];
        row.value = raw == null ? '' : String(Math.round((raw / k.threshold.scale) * 100) / 100);
      }
      if (k.direction) row.direction = r?.direction || 'both';
      return [k.on, row];
    })
  );
}

/** Per-kind editor rows -> rules array (enabled rows only, canonical order). */
export function rulesFromForm(
  formRules: Record<string, RuleFormRow | undefined> | null | undefined
): GeofenceRuleRow[] {
  const out: GeofenceRuleRow[] = [];
  for (const k of RULE_KINDS) {
    const row = formRules?.[k.on];
    if (!row?.enabled) continue;
    const rule: GeofenceRuleRow = { on: k.on, severity: row.severity || k.severity };
    if (k.threshold) {
      const n = Number(row.value);
      rule[k.threshold.key] = k.threshold.scale === 1 ? n : Math.round(n * k.threshold.scale);
    }
    if (k.direction) rule.direction = row.direction || 'both';
    out.push(rule);
  }
  return out;
}

/** Validation messages for the rules rows (empty = OK). */
export function validateRulesForm(
  formRules: Record<string, RuleFormRow | undefined> | null | undefined
): string[] {
  const errors: string[] = [];
  for (const k of RULE_KINDS) {
    const row = formRules?.[k.on];
    if (!row?.enabled) continue;
    if (k.threshold) {
      const n = Number(row.value);
      if (row.value === '' || !Number.isFinite(n) || n <= 0) {
        errors.push(`${k.label}: enter a threshold greater than 0 ${k.threshold.unit}`);
      }
    }
  }
  return errors;
}

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  const d = new Date(v as string | number | Date);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Versioned form state for an existing geofence row (or a new zone when `g`
 * is null). Rows written before 021's backfill fall back to the legacy columns.
 */
export function versionedFormFromGeofence(
  g: Partial<GeofenceRow> | null | undefined
): GeofenceVersionedForm {
  const purposeType: string = g ? purposeOf(g) : DEFAULT_PURPOSE;
  const zoneTypes = ZONE_TO_PURPOSE as Record<string, { loading: boolean; offloading: boolean }>;
  const zoneDefaults = zoneTypes[g?.ZoneType ?? ''] || ZONE_TO_PURPOSE.stockpile;
  const roles =
    g && g.LoadingRole != null
      ? { loading: !!g.LoadingRole, offloading: !!g.OffloadingRole }
      : g
        ? { loading: zoneDefaults.loading, offloading: zoneDefaults.offloading }
        : defaultRolesFor(purposeType);
  const rules = g && Array.isArray(g.Rules) ? g.Rules : rulesFromLegacy(g, purposeType);
  const schedule = isObj(g?.Schedule)
    ? (g!.Schedule as { tz?: string; windows?: ScheduleWindow[] })
    : null;
  const validFrom = toDate(g?.ValidFrom);
  const validTo = toDate(g?.ValidTo);
  const form: GeofenceVersionedForm = {
    purposeType,
    loadingRole: roles.loading,
    offloadingRole: roles.offloading,
    // ST-00282: a new zone starts with alerts off; a no-go zone is always on.
    alertsEnabled: zoneAlertsLocked(purposeType) || (g ? g.AlertsEnabled !== false : false),
    rules: rulesToForm(rules, purposeType),
    debounce: { ...DEFAULT_DEBOUNCE, ...(isObj(g?.Debounce) ? (g!.Debounce as Row) : {}) },
    scheduleEnabled: !!schedule,
    schedule: {
      tz: schedule?.tz || SCHEDULE_TZ,
      windows: schedule?.windows?.length ? schedule.windows : [{ ...DEFAULT_WINDOW }],
    },
    // A validity window is "set" when it has an end or a start still to come; a
    // past start with no end (021 stamps every version's valid_from) is simply
    // "always valid".
    validityEnabled: !!validTo || !!(validFrom && validFrom > new Date()),
    validFrom,
    validTo,
  };
  // What was loaded, so a save only sends dates the user actually changed.
  form.validityInitial = {
    enabled: form.validityEnabled,
    from: validFrom ? toApiDateString(validFrom) : null,
    to: validTo ? toApiDateString(validTo) : null,
  };
  return form;
}

/** SAST day boundaries for the validity dates the picker returns. */
export const validFromIso = (d: Date | null | undefined): string | null =>
  d ? `${toApiDateString(d)}T00:00:00+02:00` : null;
export const validToIso = (d: Date | null | undefined): string | null =>
  d ? `${toApiDateString(d)}T23:59:59+02:00` : null;

/**
 * Validity fields to send. valid_from is when a version takes effect (021 stamps
 * now() when a save leaves it alone; a written past date is retroactive), so the
 * dates go out only when the user set or changed them, and turning the window
 * off sends nulls (end removed; a start still in the future handed back to now).
 */
export function validityPayload(
  v: Pick<GeofenceVersionedForm, 'validityEnabled' | 'validFrom' | 'validTo' | 'validityInitial'>
): { validFrom?: string | null; validTo?: string | null } {
  const initial = v.validityInitial || { enabled: false, from: null, to: null };
  if (!v.validityEnabled) return initial.enabled ? { validFrom: null, validTo: null } : {};
  const out: { validFrom?: string | null; validTo?: string | null } = {};
  const from = v.validFrom ? toApiDateString(v.validFrom) : null;
  const to = v.validTo ? toApiDateString(v.validTo) : null;
  if (!initial.enabled || from !== initial.from) out.validFrom = validFromIso(v.validFrom);
  if (!initial.enabled || to !== initial.to) out.validTo = validToIso(v.validTo);
  return out;
}

/**
 * Versioned form state -> the API's versioned fields (camelCase body keys). `alertSwitch`: the
 * server has the zone Alerts switch (migration 072) — only then is `alertsEnabled` sent.
 */
export function buildVersionedPayload(
  v: GeofenceVersionedForm,
  { alertSwitch = false }: { alertSwitch?: boolean } = {}
): Row {
  const withRoles = ROLE_PURPOSES.includes(v.purposeType);
  return {
    ...(alertSwitch ? { alertsEnabled: zoneAlertsLocked(v.purposeType) || !!v.alertsEnabled } : {}),
    purposeType: v.purposeType,
    loadingRole: withRoles ? !!v.loadingRole : false,
    offloadingRole: withRoles ? !!v.offloadingRole : false,
    rules: rulesFromForm(v.rules),
    debounce: {
      entry_fixes: Number(v.debounce.entry_fixes),
      entry_seconds: Number(v.debounce.entry_seconds),
      exit_buffer_m: Number(v.debounce.exit_buffer_m),
    },
    schedule: v.scheduleEnabled
      ? { tz: v.schedule.tz || SCHEDULE_TZ, windows: v.schedule.windows }
      : null,
    ...validityPayload(v),
  };
}

/** The zone editor's fields buildSavePayload / draftGeometry read. */
export interface ZoneSaveForm {
  name?: string;
  description?: string;
  geofenceType?: string;
  zoneType?: string;
  linkedLocationId?: string | number | null;
  coordinates?: LatLngPair[] | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusMeters?: number | string | null;
}

/**
 * The zone editor's save payload. Without versioning: name, description, shape, zone type and
 * site (polygons send `coordinates`, circles latitude/longitude/radiusMeters). With versioning
 * the zone type is replaced by the versioned fields (the server derives it from the purpose).
 * Since ST-00269 neither sends the legacy alert settings (alertOnEntry, alertOnExit,
 * dwellTimeMinutes): a zone's alerts are its rules.
 */
export function buildSavePayload(
  form: ZoneSaveForm,
  versioned: GeofenceVersionedForm | null | undefined,
  { versioning = false, alertSwitch = false }: { versioning?: boolean; alertSwitch?: boolean } = {}
): Row {
  const isPolygon = form.geofenceType === 'POLYGON';
  const base: Row = {
    name: form.name,
    description: form.description,
    geofenceType: form.geofenceType,
    zoneType: form.zoneType,
    linkedLocationId:
      form.linkedLocationId === '' || form.linkedLocationId == null
        ? null
        : parseInt(String(form.linkedLocationId), 10),
  };
  const shape = isPolygon
    ? { coordinates: form.coordinates }
    : {
        latitude: form.latitude,
        longitude: form.longitude,
        radiusMeters: Number(form.radiusMeters),
      };
  if (!versioning || !versioned) return { ...base, ...shape };
  const { zoneType: _zoneType, ...rest } = base;
  return { ...rest, ...shape, ...buildVersionedPayload(versioned, { alertSwitch }) };
}

/** Geometry part of the draft for "test against history" (null = nothing drawn). */
export function draftGeometry(form: ZoneSaveForm): Row | null {
  if (form.geofenceType === 'POLYGON') {
    return (form.coordinates?.length ?? 0) >= 3
      ? { geofenceType: 'POLYGON', coordinates: form.coordinates }
      : null;
  }
  if (form.latitude == null || form.longitude == null || !(Number(form.radiusMeters) > 0)) {
    return null;
  }
  return {
    geofenceType: 'CIRCULAR',
    latitude: form.latitude,
    longitude: form.longitude,
    radiusMeters: Number(form.radiusMeters),
  };
}

/** Validation messages for the whole versioned section (empty = OK). */
export function validateVersionedForm(v: GeofenceVersionedForm): string[] {
  const errors = validateRulesForm(v.rules);
  const d = v.debounce;
  if (!(
    Number.isInteger(Number(d.entry_fixes)) &&
    Number(d.entry_fixes) >= 1 &&
    Number(d.entry_fixes) <= 50
  )) {
    errors.push('Entry fixes must be a whole number from 1 to 50');
  }
  if (!(
    Number.isInteger(Number(d.entry_seconds)) &&
    Number(d.entry_seconds) >= 0 &&
    Number(d.entry_seconds) <= 3600
  )) {
    errors.push('Entry seconds must be a whole number from 0 to 3600');
  }
  if (
    !(Number(d.exit_buffer_m) >= 0 && Number(d.exit_buffer_m) <= 1000) ||
    d.exit_buffer_m === ''
  ) {
    errors.push('Exit buffer must be 0 to 1000 m');
  }
  if (v.scheduleEnabled && !v.schedule.windows?.[0]?.days?.length) {
    errors.push('Schedule: pick at least one active day');
  }
  if (v.rules?.after_hours?.enabled && !v.scheduleEnabled) {
    errors.push('Outside operating hours: set the operating hours under Schedule and validity');
  }
  if (v.scheduleEnabled && v.schedule.windows?.[0]?.from === v.schedule.windows?.[0]?.to) {
    errors.push('Schedule: from and to must differ');
  }
  if (v.validityEnabled && (!v.validFrom || !v.validTo)) {
    errors.push('Validity: choose both dates');
  }
  return errors;
}

/**
 * A zone's alerts as the zone list shows them (ST-00269: from its rules — the legacy
 * Alert on entry / exit and Dwell alert columns are deprecated mirrors): whether entry and
 * exit raise alerts, the "dwell longer than" threshold in minutes, and how many other rules
 * the zone has. Null without rules (a database without 021).
 */
export function zoneRuleSummary(
  rules: readonly GeofenceRuleRow[] | null | undefined
): { entry: boolean; exit: boolean; dwellMinutes: number | null; others: number } | null {
  if (!Array.isArray(rules)) return null;
  const dwell = rules.find((r) => r.on === 'dwell_over');
  return {
    entry: rules.some((r) => r.on === 'entry'),
    exit: rules.some((r) => r.on === 'exit'),
    dwellMinutes:
      dwell?.threshold_s != null ? Math.round((dwell.threshold_s / 60) * 100) / 100 : null,
    others: rules.filter((r) => !['entry', 'exit', 'dwell_over'].includes(r.on)).length,
  };
}

// ── Version history ──────────────────────────────────────────────────────────

// Fields compared between two GeofenceVersionV1 rows, in display order.
export const VERSION_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'purpose_type', label: 'Purpose' },
  { key: 'roles', label: 'Roles' },
  { key: 'linked_location_id', label: 'Site' },
  { key: 'alerts_enabled', label: 'Alerts' },
  { key: 'geometry', label: 'Geometry' },
  { key: 'radius_m', label: 'Radius' },
  { key: 'rules', label: 'Rules' },
  { key: 'debounce', label: 'Debounce' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'applies_to', label: 'Applies to' },
  { key: 'valid_from', label: 'Valid from' },
  { key: 'valid_to', label: 'Valid to' },
  { key: 'is_active', label: 'Active' },
];

/** Key-order-independent JSON, so jsonb round-trips compare equal. */
export function stableStringify(v: unknown): string {
  if (v === undefined || v === null) return 'null';
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (typeof v === 'object') {
    const o = v as Row;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

const rolesOf = (row: Row | null | undefined): { loading: boolean; offloading: boolean } =>
  isObj(row?.roles)
    ? (row!.roles as { loading: boolean; offloading: boolean })
    : { loading: !!row?.loading_role, offloading: !!row?.offloading_role };

const valueOf = (row: Row | null | undefined, key: string): unknown => {
  if (key === 'roles') return rolesOf(row);
  const v = row?.[key];
  // ST-00282: a version cut before migration 072 has no switch: it alerted per its rules (on).
  if (key === 'alerts_enabled') return v !== false;
  if (key === 'valid_from' || key === 'valid_to') {
    const d = toDate(v);
    return d ? d.toISOString() : null;
  }
  return v === undefined ? null : v;
};

const dayList = (days: readonly number[] | null | undefined): string => {
  const set = new Set(days || []);
  if ([1, 2, 3, 4, 5].every((d) => set.has(d)) && set.size === 5) return 'Mon-Fri';
  if (set.size === 7) return 'Every day';
  return WEEKDAYS.filter((d) => set.has(d.id))
    .map((d) => d.short)
    .join(', ');
};
const windowsText = (w: { windows?: ScheduleWindow[] } | null | undefined): string =>
  (w?.windows || []).map((x) => `${dayList(x.days)} ${x.from}-${x.to}`).join('; ');

function ruleText(r: GeofenceRuleRow): string {
  const kind = RULE_KINDS.find((k) => k.on === r.on);
  const label = kind?.label || r.on;
  let detail = '';
  if (r.threshold_s != null) detail = ` ${Math.round((r.threshold_s / 60) * 100) / 100} min`;
  if (r.limit_kmh != null) detail = ` ${r.limit_kmh} km/h`;
  if (r.on === 'after_hours') {
    const d = AFTER_HOURS_DIRECTIONS.find((x) => x.id === (r.direction || 'both'));
    detail = ` (${(d?.label || r.direction || '').toLowerCase()})`;
  }
  return `${label}${detail} [${r.severity || '?'}]`;
}

function geometryText(g: unknown): string {
  if (!isObj(g)) return '—';
  const coordinates = g.coordinates as unknown;
  if (g.type === 'Point') {
    const [lon, lat] = (coordinates as number[] | undefined) || [];
    return `Circle centre ${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
  }
  if (g.type === 'Polygon') {
    const ring = (coordinates as unknown[][] | undefined)?.[0] || [];
    return `Polygon, ${Math.max(0, ring.length - 1)} vertices`;
  }
  return (g.type as string) || '—';
}

/** Human-readable value of one version field. */
export function formatVersionValue(key: string, row: Row | null | undefined): string {
  const v = valueOf(row, key);
  if (v === null || v === '') return '—';
  switch (key) {
    case 'purpose_type':
      return getPurposeMeta(v as string)!.label;
    case 'roles': {
      const roles = v as { loading: boolean; offloading: boolean };
      const r = [roles.loading && 'Loading', roles.offloading && 'Offloading'].filter(Boolean);
      return r.length ? r.join(' + ') : 'None';
    }
    case 'geometry':
      return geometryText(v);
    case 'radius_m':
      return `${v} m`;
    case 'rules':
      return Array.isArray(v) && v.length
        ? (v as GeofenceRuleRow[]).map(ruleText).join('; ')
        : 'No rules';
    case 'debounce': {
      const d = v as { entry_fixes: number; entry_seconds: number; exit_buffer_m: number };
      return `Entry after ${d.entry_fixes} fixes / ${d.entry_seconds} s; exit beyond ${d.exit_buffer_m} m`;
    }
    case 'schedule':
      return windowsText(v as { windows?: ScheduleWindow[] }) || 'Always';
    case 'applies_to': {
      const a = v as { all_assets?: boolean; asset_groups?: string[]; routes?: string[] };
      if (a.all_assets) return 'All assets';
      if (a.asset_groups) return `Asset groups: ${a.asset_groups.join(', ')}`;
      if (a.routes) return `Routes: ${a.routes.join(', ')}`;
      return stableStringify(v);
    }
    case 'valid_from':
    case 'valid_to':
      return formatAppDateTime(v as string);
    case 'is_active':
      return v ? 'Yes' : 'No';
    case 'alerts_enabled':
      return v === false ? 'Off' : 'On';
    default:
      return String(v);
  }
}

/** One differing field between two versions, with display strings. */
export interface VersionDiff {
  key: string;
  label: string;
  before: string;
  after: string;
}

/**
 * The fields that differ between versions `a` and `b`:
 * [{ key, label, before, after }] with display strings.
 */
export function diffVersions(a: Row | null | undefined, b: Row | null | undefined): VersionDiff[] {
  if (!a || !b) return [];
  return VERSION_FIELDS.filter(
    (f) => stableStringify(valueOf(a, f.key)) !== stableStringify(valueOf(b, f.key))
  ).map((f) => ({
    key: f.key,
    label: f.label,
    before: formatVersionValue(f.key, a),
    after: formatVersionValue(f.key, b),
  }));
}

/** Labels of the fields a version changed relative to the one before it. */
export function changedFieldLabels(
  prev: Row | null | undefined,
  cur: Row | null | undefined
): string[] {
  if (!prev) return ['Created'];
  const d = diffVersions(prev, cur);
  return d.length ? d.map((x) => x.label) : ['No field changes'];
}

/** Who/when of a version row (created_by / created_at per contract §3.2). */
export const versionAuthor = (v: Row | null | undefined): string =>
  (v?.created_by as string) || (v?.actor as string) || 'unknown';
export const versionTime = (v: Row | null | undefined): string | null =>
  (v?.created_at as string) || (v?.recorded_at as string) || (v?.valid_from as string) || null;
